import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { format } from '../../../utils/date'

interface Props {
  list: {
    label: string
    currentValue: number
    pastValue: number
  }[]
  chartSize: number
  gap?: boolean
  labelSkip?: number
  targetDate: string
  prevDate: string
  maxValue?: number
}

const LineChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    gap,
    labelSkip,
    targetDate,
    prevDate,
    maxValue,
  } = props

  const graphRef = useRef<HTMLCanvasElement>(null)

  const [selectLabel, setSelectLabel] = useState<string | null>(null)

  const maxLimit = useMemo(() => {
    if (!maxValue || maxValue <= 1500) return 1500
    if (maxValue <= 2400) {
      return Math.ceil(maxValue / 300) * 300
    }
    if (maxValue > 2400) {
      return Math.ceil(maxValue / 600) * 600
    }
    return 1500
  }, [list])

  const limitSpan = useMemo(() => {
    if (maxLimit <= 2400) {
      return 300
    }
    if (maxLimit > 2400) {
      return 600
    }
    return 300
  }, [maxLimit])

  const buildUnit = (value: number) => (
    <>
      <span>
        {value.toLocaleString() || "0"}
      </span>
      biz
    </>
  )

  useEffect(() => {
    const width = graphRef.current?.clientWidth || 0
    const height = graphRef.current?.clientHeight || 0
    const canvas = graphRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.width = width
    canvas.height = height
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = "#DDDDDD"
    const guideCount = Math.ceil(maxLimit / limitSpan)
    Array(guideCount + 1).fill(null).forEach((_v, i) => {
      ctx.moveTo(0, (height / guideCount) * i)
      ctx.lineTo(width, (height / guideCount) * i)
    })
    Array(25).fill(null).forEach((_v, i) => {
      ctx.moveTo((width / 24) * i, 0)
      ctx.lineTo((width / 24) * i, height)
    })
    ctx.stroke()

    // past
    ctx.beginPath()
    ctx.lineWidth = 1.5
    ctx.strokeStyle = "transparent"
    ctx.lineTo(0, height)
    list.forEach((value, index) => {
      ctx.lineTo(
        ((width / (list.length - 1)) * index) - (index * 0.05),
        height - ((value.pastValue / maxLimit) * height) ? height - ((value.pastValue / maxLimit) * height) - 1 : 0,
      )
    })
    ctx.lineTo(width, height)
    const grad_past = ctx.createLinearGradient(0, 0, 0, height)
    grad_past.addColorStop(0, "#D2D6DE")
    grad_past.addColorStop(1, "#E7EAEE")
    ctx.fillStyle = grad_past
    ctx.fill()
    ctx.stroke()

    // current
    ctx.beginPath()
    ctx.lineWidth = 1.5
    ctx.strokeStyle = "transparent"
    ctx.lineTo(0, height)
    list.forEach((value, index) => {
      ctx.lineTo(
        ((width / (list.length - 1)) * index) - (index * 0.05),
        height - ((value.currentValue / maxLimit) * height) ? height - ((value.currentValue / maxLimit) * height) - 1 : 0,
      )
    })
    ctx.lineTo(width, height)
    const grad = ctx.createLinearGradient(0, 0, 0, height)
    grad.addColorStop(0, "#3C8DBC")
    grad.addColorStop(1, "#98c1db")
    ctx.fillStyle = grad
    ctx.fill()
    ctx.stroke()
  }, [location])

  return (
    <div className="line_chart_wrap">
      <div className="vertical" style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
            margin: "16px 0 8px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexFlow: "column-reverse",
              height: `${chartSize + 10}px`,
              textAlign: "right",
              color: "#999999",
              width: "40px",
              marginTop: "-4px",
            }}
          >
            {
              Array(Math.ceil(maxLimit / limitSpan) + 1).fill(null).map((_v, i) => (
                <div
                  key={uniqueId("val_")}
                >
                  {i * limitSpan}
                </div>
              ))
            }
          </div>
          <div
            className="line_chart_area"
            style={{
              height: `${chartSize}px`,
              position: "relative",
              width: "100%",
              justifyContent: gap ? "space-evenly" : "center",
            }}
          >
            <canvas
              ref={graphRef}
              style={{
                height: `${chartSize}px`,
                width: "100%",
                zIndex: 1,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "0",
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                textAlign: "center",
              }}
            >
              {
                list.map((value, index) => (
                  <div
                    style={{
                      position: "relative",
                      width: `calc(100% / ${list.length || 1})`,
                      height: `calc(${chartSize}px + 20px)`,
                      display: "flex",
                      alignItems: "flex-end",
                      transform: "translateX(-50%)",
                      color: "#666666",
                    }}
                    onMouseOver={() => setSelectLabel(value.label)}
                    onFocus={() => { /* */ }}
                    onMouseLeave={() => setSelectLabel(null)}
                    key={uniqueId("line_")}
                  >
                    {
                      labelSkip && (index % labelSkip) !== 0 ? (
                        <></>
                      ) : (
                        <div>
                          {value.label}
                        </div>
                      )
                    }
                    {
                      selectLabel === value.label && (
                        <>
                          <div
                            style={{
                              position: "absolute",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: "#98c1db",
                              left: "50%",
                              bottom: `${(chartSize * (value.currentValue / maxLimit)) + 20}px`,
                              transform: "translate(-50%, 50%)",
                              zIndex: 1,
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              bottom: `${(chartSize * (value.currentValue / maxLimit)) + 0}px`,
                              transform: "translate(-50%, -50%)",
                              color: "#666666",
                              zIndex: 1,
                              border: "solid 1px #EEEEEE",
                              borderRadius: "4px",
                              backgroundColor: "#FFFFFF",
                              minWidth: "80px",
                              textAlign: "left",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#EEEEEE",
                                borderRadius: "4px 4px 0 0",
                                padding: "4px 8px",
                              }}
                            >
                              {value.label}
                            </div>
                            <div
                              style={{
                                padding: "4px 8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    backgroundColor: "#D2D6DE",
                                  }}
                                />
                                {`${format(prevDate, "MM/DD")}:`}
                                <div
                                  style={{
                                    fontWeight: "bold",
                                  }}
                                >
                                  {buildUnit(value.pastValue)}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    backgroundColor: "#3C8DBC",
                                  }}
                                />
                                {`${format(targetDate, "MM/DD")}:`}
                                <div
                                  style={{
                                    fontWeight: "bold",
                                  }}
                                >
                                  {buildUnit(value.currentValue)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )
                    }
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

LineChart.defaultProps = {
  gap: true,
  labelSkip: 0,
  maxValue: 1500,
}

export default LineChart
